/**
 * Client → Data → Client Details and Files, as AgencyAnalytics': what the
 * agency knows about a client (industry, audience, seasonality …) and its
 * documents (PDF, images, text, CSV; up to 20 of 10 MB). SynAssistant reads
 * both before advising on the client.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { apiCall, apiDownload, apiUpload, orgPath } from '../api.ts';
import type { Client } from '../reports/reports-api.ts';

interface Field {
  key: string;
  label: string;
}

interface ClientFile {
  id: string;
  name: string;
  content_type: string;
  size_bytes: number;
  created_at: string;
}

const ACCEPT =
  '.pdf,.png,.jpg,.jpeg,.webp,.gif,.txt,.md,.csv,application/pdf,image/*,text/plain,text/markdown,text/csv';
const KILOBYTE = 1024;

export function ClientDetailsPanel({
  organisationId,
  client,
}: {
  organisationId: string;
  client: Client;
}): ReactNode {
  const [fields, setFields] = useState<Field[]>([]);
  const [profile, setProfile] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<string | null>(null);
  const path = orgPath(organisationId, `/reports/clients/${client.id}/profile`);
  useEffect(() => {
    apiCall<{ fields: Field[]; profile: Record<string, string> }>(path).then(
      (data) => {
        setFields(data.fields);
        setProfile(data.profile);
      },
      () => setStatus('Client details could not be loaded.'),
    );
  }, [path]);
  const save = () => {
    setStatus('Saving…');
    apiCall<{ profile: Record<string, string> }>(path, {
      method: 'PUT',
      body: JSON.stringify({ profile }),
    }).then(
      (data) => {
        setProfile(data.profile);
        setStatus('Saved. SynAssistant will use these details.');
      },
      (error: unknown) => setStatus(error instanceof Error ? error.message : 'Not saved.'),
    );
  };
  return (
    <section className="card ck" aria-labelledby="ck-title">
      <h2 id="ck-title">Client Details</h2>
      <p className="panel-note">
        What SynAssistant should know about {client.name} when it reads their numbers.
      </p>
      <div className="ck-grid">
        {fields.map((field) => (
          <label key={field.key} className="rs-field">
            {field.label}
            <textarea
              rows={2}
              maxLength={2000}
              value={profile[field.key] ?? ''}
              onChange={(event) => setProfile({ ...profile, [field.key]: event.target.value })}
            />
          </label>
        ))}
      </div>
      <div className="wl-row">
        <button type="button" className="btn" onClick={save}>
          Save details
        </button>
        <span className="kp-faint" role="status">
          {status}
        </span>
      </div>
    </section>
  );
}

export function ClientFilesPanel({
  organisationId,
  client,
}: {
  organisationId: string;
  client: Client;
}): ReactNode {
  const [files, setFiles] = useState<ClientFile[] | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = orgPath(organisationId, `/reports/clients/${client.id}/files`);
  const load = () =>
    apiCall<{ files: ClientFile[] }>(list).then(
      (data) => setFiles(data.files),
      () => setFiles([]),
    );
  useEffect(() => void load(), [list]);
  const upload = (file: File) => {
    const form = new FormData();
    form.append('file', file);
    setStatus(`Uploading ${file.name}…`);
    apiUpload(list, form).then(
      () => {
        setStatus(`${file.name} added.`);
        void load();
      },
      (error: unknown) =>
        setStatus(error instanceof Error ? error.message : 'The file could not be added.'),
    );
  };
  const download = (file: ClientFile) =>
    apiDownload(orgPath(organisationId, `/reports/files/${file.id}`)).then((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
  return (
    <section className="card ck" aria-labelledby="cf-files-title">
      <h2 id="cf-files-title">Files</h2>
      <p className="panel-note">
        Contracts, brand guides, meeting notes: SynAssistant can read them. PDF, images, text,
        Markdown or CSV; up to 20 files of 10 MB.
      </p>
      <div className="wl-row">
        <input
          ref={input}
          type="file"
          accept={ACCEPT}
          className="sr-only"
          id="ck-upload"
          onChange={(event) => event.target.files?.[0] && upload(event.target.files[0])}
        />
        <label htmlFor="ck-upload" className="btn secondary">
          Add file
        </label>
        <span className="kp-faint" role="status">
          {status}
        </span>
      </div>
      {files && files.length > 0 && (
        <ul className="as-list">
          {files.map((file) => (
            <li key={file.id}>
              <span className="as-list-main">
                <strong>{file.name}</strong>
                <small>{Math.max(1, Math.round(file.size_bytes / KILOBYTE))} KB</small>
              </span>
              <button type="button" className="ex-link" onClick={() => void download(file)}>
                Download
              </button>
              <button
                type="button"
                className="ex-link"
                onClick={() =>
                  void apiCall(orgPath(organisationId, `/reports/files/${file.id}`), {
                    method: 'DELETE',
                  }).then(load)
                }
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {files?.length === 0 && <p className="kp-faint">No files yet.</p>}
    </section>
  );
}
